import { CONTACT_POINT_SYSTEM } from "utils/constants";

export const DEFAULT_ADDRESS_COUNTRY = "France";

export const ACORIS_ZIP_CODE_LENGTH = 5;

export const ACORIS_ZIP_CODE_PATTERN = /^(0[1-9]|[1-8][0-9]|9[0-9])[0-9]{3}$/;

export const FOREIGN_RESIDENT_ZIP_CODE_PATTERN = /^99[0-9]{3}$/;

const toZip = (zipCode) => String(zipCode || "").trim();

export const isCompleteAcorisZipInput = (zipCode) =>
  toZip(zipCode).length === ACORIS_ZIP_CODE_LENGTH;

export const isAcorisZipCode = (zipCode) =>
  ACORIS_ZIP_CODE_PATTERN.test(toZip(zipCode));

export const isForeignResidentZipCode = (zipCode) =>
  FOREIGN_RESIDENT_ZIP_CODE_PATTERN.test(toZip(zipCode));

export const normalizeAcorisZipInput = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, ACORIS_ZIP_CODE_LENGTH);

export const resolveDefaultAddressCountry = (zipCode) =>
  isForeignResidentZipCode(zipCode) ? null : DEFAULT_ADDRESS_COUNTRY;

export const getAddressResetAfterZipChange = ({
  previousZipCode,
  nextZipCode,
}) => {
  if (!isCompleteAcorisZipInput(nextZipCode)) {
    return { city: null, country: undefined };
  }

  if (!isCompleteAcorisZipInput(previousZipCode)) {
    return {
      city: null,
      country: resolveDefaultAddressCountry(nextZipCode),
    };
  }

  const previousForeign = isForeignResidentZipCode(previousZipCode);
  const nextForeign = isForeignResidentZipCode(nextZipCode);
  return {
    city: null,
    country:
      previousForeign === nextForeign
        ? undefined
        : resolveDefaultAddressCountry(nextZipCode),
  };
};

export const applyAcorisZipFieldChange = ({
  lastCompleteZipCode,
  rawValue,
}) => {
  const nextZip = normalizeAcorisZipInput(rawValue);
  return {
    nextZip,
    reset: getAddressResetAfterZipChange({
      previousZipCode: lastCompleteZipCode,
      nextZipCode: nextZip,
    }),
    nextCompleteZipCode: isCompleteAcorisZipInput(nextZip)
      ? nextZip
      : lastCompleteZipCode,
  };
};

export const hasRequiredAddressCountry = (zipCode, country) => {
  if (!isForeignResidentZipCode(zipCode)) return true;
  return Boolean(String(country || "").trim());
};

export const resolveForeignZipUi = (zipCode, lastCompleteZipCode) =>
  isForeignResidentZipCode(
    isCompleteAcorisZipInput(zipCode) ? zipCode : lastCompleteZipCode,
  );

export const getAcorisCityPlaceholder = (cityCopy, isForeignZip) =>
  isForeignZip ? cityCopy?.FREE_PLACEHOLDER : cityCopy?.PLACEHOLDER;

export const attachAcorisForeignCountryTest = (schema, requiredMessage) =>
  schema.test("foreign-country", requiredMessage, function (address) {
    const zipCode = address?.[CONTACT_POINT_SYSTEM.ZIP_CODE]?.value;
    const country = address?.[CONTACT_POINT_SYSTEM.COUNTRY]?.value;
    if (hasRequiredAddressCountry(zipCode, country)) return true;
    return this.createError({
      path: `${CONTACT_POINT_SYSTEM.COUNTRY}.value`,
      message: requiredMessage,
    });
  });

export const validateAcorisZipCode = async (value, fetchCities) => {
  if (!isAcorisZipCode(value)) return false;
  if (isForeignResidentZipCode(value) || typeof fetchCities !== "function") {
    return true;
  }
  const cities = await fetchCities(value);
  return Boolean(cities && cities.length);
};
